import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { GitBranch, LayoutTemplate, List, Loader2, Pencil, Plus, Trash2, Zap } from 'lucide-react';
import { getEmailApiBaseUrl } from '../../../utils/emailApi';
import LoadingSpinner from '../../common/LoadingSpinner';
import GymEmailAutomationFlowCanvas from './GymEmailAutomationFlowCanvas';
import GymEmailAutomationRunsPanel from './GymEmailAutomationRunsPanel';
import GymEmailAutomationRuleDetails from './GymEmailAutomationRuleDetails';
import {
  gymMarketingCardInner,
  gymMarketingMainCol,
  gymMarketingSideCol,
  gymMarketingSplitGrid,
  gymModalInput,
  gymModalLabel,
  gymSocialBtnPrimary,
  gymSocialBtnSecondary,
  gymSocialCard,
  gymSocialMutedText,
  gymContentTabActive,
  gymContentTabInactive,
  gymSocialInsetBox,
  gymSocialBtnDanger,
  gymSocialControlLabel,
  gymSocialItemText,
  gymSocialListTitle,
  gymSocialStepIndex,
  gymSocialTitleText,
} from '../gymPageTheme';

const PUSH_VARIABLES = ['{{nome}}', '{{palestra}}'];

const EMPTY_STEP = { action_type: 'tag.add', config: { tag_id: '', duration_days: 30 } };

function defaultStepConfig(actionType) {
  switch (actionType) {
    case 'tag.add':
      return { tag_id: '', duration_days: 30 };
    case 'tag.remove':
      return { tag_id: '' };
    case 'wait':
      return { days: 0, hours: 0 };
    case 'email.send':
      return { template_id: '' };
    case 'push.send':
      return { title: '', body: '', type: 'marketing', priority: 'normal' };
    default:
      return {};
  }
}
const HIDDEN_TRIGGERS = ['schedule.daily'];
const TRIGGER_CONFIG_DEFAULTS = {
  'member.inactive': { days: 30 },
  'subscription.expiring': { days_before: 7 },
};

function defaultConditions() {
  return [{ type: 'marketing_opt_in' }, { type: 'membership_active' }];
}

export default function GymEmailAutomationsPanel({ api }) {
  const { t } = useTranslation();
  const emailApi = getEmailApiBaseUrl();
  const [section, setSection] = useState('rules');
  const [editorMode, setEditorMode] = useState('form');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState({ triggers: [], actions: [], conditions: [] });
  const [automations, setAutomations] = useState([]);
  const [tags, setTags] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState('');
  const [triggerCode, setTriggerCode] = useState('member.created');
  const [triggerConfig, setTriggerConfig] = useState({});
  const [conditions, setConditions] = useState(defaultConditions());
  const [steps, setSteps] = useState([{ ...EMPTY_STEP }]);
  const [expandedListId, setExpandedListId] = useState(null);
  const [listDetailsCache, setListDetailsCache] = useState({});
  const [listDetailsLoading, setListDetailsLoading] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [metaRes, listRes, tagsRes, tplRes] = await Promise.all([
        api.get('gym_email_automations.php?action=meta', { baseURL: emailApi }),
        api.get('gym_email_automations.php?action=list', { baseURL: emailApi }),
        api.get('gym_email_tags.php?action=list', { baseURL: emailApi }),
        api.get('gym_email_templates.php?action=list', { baseURL: emailApi }),
      ]);
      setMeta(metaRes.data || { triggers: [], actions: [], conditions: [] });
      setAutomations(listRes.data?.automations || []);
      setTags(tagsRes.data?.tags || []);
      setTemplates(tplRes.data?.templates || []);
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.email.automations.loadError'));
    } finally {
      setLoading(false);
    }
  }, [api, emailApi, t]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const visibleTriggers = useMemo(
    () => (meta.triggers || []).filter((tr) => !HIDDEN_TRIGGERS.includes(tr.code)),
    [meta.triggers]
  );

  const triggerLabel = useMemo(
    () => visibleTriggers.find((tr) => tr.code === triggerCode)?.label || triggerCode,
    [visibleTriggers, triggerCode]
  );

  const resetForm = () => {
    setEditingId(null);
    setName('');
    setTriggerCode('member.created');
    setTriggerConfig({});
    setConditions(defaultConditions());
    setSteps([{ ...EMPTY_STEP }]);
    setEditorMode('form');
  };

  const loadForEdit = async (id) => {
    try {
      const res = await api.get(`gym_email_automations.php?action=get&id=${id}`, { baseURL: emailApi });
      const a = res.data?.automation;
      if (!a) return;
      setEditingId(a.id);
      setName(a.name || '');
      setTriggerCode(a.trigger_code || 'member.created');
      setTriggerConfig({ ...(a.trigger_config || {}) });
      setConditions((a.conditions || []).length > 0 ? [...a.conditions] : []);
      setSteps(
        (a.steps || []).length > 0
          ? a.steps.map((s) => ({ action_type: s.action_type, config: { ...(s.config || {}) } }))
          : [{ ...EMPTY_STEP }]
      );
      setSection('rules');
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.email.automations.loadError'));
    }
  };

  const handleTriggerChange = (code) => {
    setTriggerCode(code);
    setTriggerConfig({ ...(TRIGGER_CONFIG_DEFAULTS[code] || {}) });
  };

  const buildPayload = () => {
    const cfg = { ...triggerConfig };
    if (triggerCode === 'tag.added' && cfg.tag_id) {
      cfg.tag_id = parseInt(cfg.tag_id, 10);
    } else if (triggerCode === 'tag.added') {
      delete cfg.tag_id;
    }
    if (triggerCode === 'member.inactive' && cfg.days !== undefined) {
      cfg.days = parseInt(cfg.days, 10) || 30;
    }
    if (triggerCode === 'subscription.expiring' && cfg.days_before !== undefined) {
      cfg.days_before = parseInt(cfg.days_before, 10) || 7;
    }

    const normalizedConditions = conditions.map((c) => {
      if (c.type === 'has_tag' || c.type === 'not_has_tag') {
        return { type: c.type, tag_id: parseInt(c.tag_id, 10) };
      }
      return { type: c.type };
    });

    const normalizedSteps = steps.map((step, idx) => {
      const stepCfg = { ...(step.config || {}) };
      if (stepCfg.tag_id !== undefined && stepCfg.tag_id !== '') stepCfg.tag_id = parseInt(stepCfg.tag_id, 10);
      if (stepCfg.template_id !== undefined && stepCfg.template_id !== '') stepCfg.template_id = parseInt(stepCfg.template_id, 10);
      if (stepCfg.duration_days !== undefined && stepCfg.duration_days !== '') stepCfg.duration_days = parseInt(stepCfg.duration_days, 10);
      if (stepCfg.days !== undefined && stepCfg.days !== '') stepCfg.days = parseInt(stepCfg.days, 10);
      if (stepCfg.hours !== undefined && stepCfg.hours !== '') stepCfg.hours = parseInt(stepCfg.hours, 10);
      return { sort_order: idx + 1, action_type: step.action_type, config: stepCfg };
    });

    return {
      id: editingId,
      name: name.trim(),
      trigger_code: triggerCode,
      trigger_config: cfg,
      conditions: normalizedConditions,
      steps: normalizedSteps,
      is_active: true,
    };
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error(t('gym.email.automations.nameRequired'));
      return;
    }
    const invalidPush = steps.find(
      (s) => s.action_type === 'push.send' && (!String(s.config?.title || '').trim() || !String(s.config?.body || '').trim())
    );
    if (invalidPush) {
      toast.error(t('gym.email.automations.pushFieldsRequired'));
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      const action = editingId ? 'update' : 'create';
      await api.post(`gym_email_automations.php?action=${action}`, payload, { baseURL: emailApi });
      toast.success(t('gym.email.automations.saved'));
      resetForm();
      setListDetailsCache({});
      setExpandedListId(null);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.email.automations.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id, isActive) => {
    try {
      await api.post(
        'gym_email_automations.php?action=toggle',
        { id, is_active: !isActive },
        { baseURL: emailApi }
      );
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.email.automations.saveError'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('gym.email.automations.deleteConfirm'))) return;
    try {
      await api.post('gym_email_automations.php?action=delete', { id }, { baseURL: emailApi });
      toast.success(t('gym.email.automations.deleted'));
      if (editingId === id) resetForm();
      if (expandedListId === id) setExpandedListId(null);
      setListDetailsCache((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.email.automations.saveError'));
    }
  };

  const toggleListDetails = async (id) => {
    if (expandedListId === id) {
      setExpandedListId(null);
      return;
    }
    setExpandedListId(id);
    if (listDetailsCache[id]) return;
    setListDetailsLoading(id);
    try {
      const res = await api.get(`gym_email_automations.php?action=get&id=${id}`, { baseURL: emailApi });
      const detail = res.data?.automation;
      if (detail) {
        setListDetailsCache((prev) => ({ ...prev, [id]: detail }));
      }
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.email.automations.loadError'));
      setExpandedListId(null);
    } finally {
      setListDetailsLoading(null);
    }
  };

  const updateStep = (index, patch) => {
    setSteps((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  };

  const updateStepConfig = (index, key, value) => {
    setSteps((prev) =>
      prev.map((s, i) =>
        i === index ? { ...s, config: { ...(s.config || {}), [key]: value } } : s
      )
    );
  };

  const insertPushVariable = (index, field, variable) => {
    setSteps((prev) => {
      const step = prev[index];
      const current = String(step?.config?.[field] ?? '');
      const el = document.activeElement;
      const isField =
        (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) &&
        el.dataset.pushField === field &&
        String(el.dataset.stepIndex) === String(index);

      let next;
      let selectionPos;
      if (isField) {
        const start = el.selectionStart ?? current.length;
        const end = el.selectionEnd ?? start;
        next = current.slice(0, start) + variable + current.slice(end);
        selectionPos = start + variable.length;
      } else {
        next = current + variable;
      }

      if (isField && selectionPos !== undefined) {
        requestAnimationFrame(() => {
          el.focus();
          el.setSelectionRange(selectionPos, selectionPos);
        });
      }

      return prev.map((s, i) =>
        i === index ? { ...s, config: { ...(s.config || {}), [field]: next } } : s
      );
    });
  };

  const renderPushVariableChips = (index, field) => (
    <div className="mt-1 flex flex-wrap items-center gap-2">
      <span className={`text-xs ${gymSocialMutedText}`}>{t('gym.email.campaigns.insertVariable')}</span>
      {PUSH_VARIABLES.map((variable) => (
        <button
          key={variable}
          type="button"
          className={`${gymSocialBtnSecondary} px-2.5 py-1 text-xs font-mono`}
          title={t('gym.email.campaigns.insertVariableTitle', { variable })}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => insertPushVariable(index, field, variable)}
        >
          {variable}
        </button>
      ))}
    </div>
  );

  const addCondition = (type) => {
    if (conditions.some((c) => c.type === type)) return;
    if (type === 'has_tag' || type === 'not_has_tag') {
      setConditions((prev) => [...prev, { type, tag_id: '' }]);
    } else {
      setConditions((prev) => [...prev, { type }]);
    }
  };

  const updateConditionTag = (index, tagId) => {
    setConditions((prev) => prev.map((c, i) => (i === index ? { ...c, tag_id: tagId } : c)));
  };

  const removeCondition = (index) => {
    setConditions((prev) => prev.filter((_, i) => i !== index));
  };

  const renderTriggerConfig = () => {
    if (triggerCode === 'tag.added') {
      return (
        <div>
          <label className={gymModalLabel}>{t('gym.email.automations.triggerTag')}</label>
          <select
            className={gymModalInput}
            value={triggerConfig.tag_id ?? ''}
            onChange={(e) => setTriggerConfig((c) => ({ ...c, tag_id: e.target.value }))}
          >
            <option value="">{t('gym.email.automations.anyTag')}</option>
            {tags.map((tg) => (
              <option key={tg.id} value={tg.id}>{tg.name}</option>
            ))}
          </select>
        </div>
      );
    }
    if (triggerCode === 'member.inactive') {
      return (
        <div>
          <label className={gymModalLabel}>{t('gym.email.automations.inactiveDays')}</label>
          <input
            type="number"
            min={1}
            className={gymModalInput}
            value={triggerConfig.days ?? 30}
            onChange={(e) => setTriggerConfig((c) => ({ ...c, days: e.target.value }))}
          />
          <p className={`${gymSocialMutedText} text-xs mt-1`}>{t('gym.email.automations.inactiveHint')}</p>
        </div>
      );
    }
    if (triggerCode === 'subscription.expiring') {
      return (
        <div>
          <label className={gymModalLabel}>{t('gym.email.automations.expiringDaysBefore')}</label>
          <input
            type="number"
            min={0}
            className={gymModalInput}
            value={triggerConfig.days_before ?? 7}
            onChange={(e) => setTriggerConfig((c) => ({ ...c, days_before: e.target.value }))}
          />
        </div>
      );
    }
    if (triggerCode === 'member.birthday') {
      return (
        <p className={`${gymSocialMutedText} text-xs`}>{t('gym.email.automations.birthdayHint')}</p>
      );
    }
    return null;
  };

  const availableConditions = useMemo(
    () => (meta.conditions || []).filter((c) => !conditions.some((cond) => cond.type === c.code)),
    [meta.conditions, conditions]
  );

  const renderStepFields = (step, index) => {
    const type = step.action_type;
    if (type === 'tag.add' || type === 'tag.remove') {
      return (
        <div className="grid gap-2 sm:grid-cols-2">
          <div>
            <label className={gymModalLabel}>{t('gym.email.automations.stepTag')}</label>
            <select
              className={gymModalInput}
              value={step.config?.tag_id ?? ''}
              onChange={(e) => updateStepConfig(index, 'tag_id', e.target.value)}
            >
              <option value="">{t('gym.email.automations.selectTag')}</option>
              {tags.filter((tg) => !tg.is_system).map((tg) => (
                <option key={tg.id} value={tg.id}>{tg.name}</option>
              ))}
            </select>
          </div>
          {type === 'tag.add' && (
            <div>
              <label className={gymModalLabel}>{t('gym.email.automations.durationDays')}</label>
              <input
                type="number"
                min={0}
                className={gymModalInput}
                value={step.config?.duration_days ?? ''}
                onChange={(e) => updateStepConfig(index, 'duration_days', e.target.value)}
                placeholder="0 = permanente"
              />
            </div>
          )}
        </div>
      );
    }
    if (type === 'wait') {
      return (
        <div className="grid gap-2 sm:grid-cols-2">
          <div>
            <label className={gymModalLabel}>{t('gym.email.automations.waitDays')}</label>
            <input
              type="number"
              min={0}
              className={gymModalInput}
              value={step.config?.days ?? 0}
              onChange={(e) => updateStepConfig(index, 'days', e.target.value)}
            />
          </div>
          <div>
            <label className={gymModalLabel}>{t('gym.email.automations.waitHours')}</label>
            <input
              type="number"
              min={0}
              className={gymModalInput}
              value={step.config?.hours ?? 0}
              onChange={(e) => updateStepConfig(index, 'hours', e.target.value)}
            />
          </div>
        </div>
      );
    }
    if (type === 'email.send') {
      return (
        <div>
          <label className={gymModalLabel}>{t('gym.email.automations.stepTemplate')}</label>
          <select
            className={gymModalInput}
            value={step.config?.template_id ?? ''}
            onChange={(e) => updateStepConfig(index, 'template_id', e.target.value)}
          >
            <option value="">{t('gym.email.automations.selectTemplate')}</option>
            {templates.map((tpl) => (
              <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
            ))}
          </select>
        </div>
      );
    }
    if (type === 'push.send') {
      return (
        <div className="space-y-2">
          <div>
            <label className={gymModalLabel}>{t('gym.email.automations.pushTitle')}</label>
            {renderPushVariableChips(index, 'title')}
            <input
              type="text"
              className={gymModalInput}
              data-push-field="title"
              data-step-index={index}
              value={step.config?.title ?? ''}
              onChange={(e) => updateStepConfig(index, 'title', e.target.value)}
              placeholder={t('gym.email.automations.pushTitlePlaceholder')}
            />
          </div>
          <div>
            <label className={gymModalLabel}>{t('gym.email.automations.pushBody')}</label>
            {renderPushVariableChips(index, 'body')}
            <textarea
              className={gymModalInput}
              rows={3}
              data-push-field="body"
              data-step-index={index}
              value={step.config?.body ?? ''}
              onChange={(e) => updateStepConfig(index, 'body', e.target.value)}
              placeholder={t('gym.email.automations.pushBodyPlaceholder')}
            />
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <div>
              <label className={gymModalLabel}>{t('gym.email.automations.pushType')}</label>
              <select
                className={gymModalInput}
                value={step.config?.type ?? 'marketing'}
                onChange={(e) => updateStepConfig(index, 'type', e.target.value)}
              >
                <option value="marketing">{t('gym.email.automations.pushTypeMarketing')}</option>
                <option value="reminder">{t('gym.email.automations.pushTypeReminder')}</option>
                <option value="transactional">{t('gym.email.automations.pushTypeTransactional')}</option>
              </select>
            </div>
            <div>
              <label className={gymModalLabel}>{t('gym.email.automations.pushPriority')}</label>
              <select
                className={gymModalInput}
                value={step.config?.priority ?? 'normal'}
                onChange={(e) => updateStepConfig(index, 'priority', e.target.value)}
              >
                <option value="normal">{t('gym.email.automations.pushPriorityNormal')}</option>
                <option value="high">{t('gym.email.automations.pushPriorityHigh')}</option>
              </select>
              <p className={`${gymSocialMutedText} text-xs mt-1`}>{t('gym.email.automations.pushPriorityHint')}</p>
            </div>
          </div>
          <p className={`${gymSocialMutedText} text-xs`}>{t('gym.email.automations.pushInboxHint')}</p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className={section === 'rules' ? gymContentTabActive : gymContentTabInactive}
          onClick={() => setSection('rules')}
        >
          <GitBranch className="h-4 w-4 inline mr-1" />
          {t('gym.email.automations.sectionRules')}
        </button>
        <button
          type="button"
          className={section === 'runs' ? gymContentTabActive : gymContentTabInactive}
          onClick={() => setSection('runs')}
        >
          <List className="h-4 w-4 inline mr-1" />
          {t('gym.email.automations.sectionRuns')}
        </button>
      </div>

      {section === 'runs' ? (
        <GymEmailAutomationRunsPanel api={api} automations={automations} />
      ) : (
        <div className={gymMarketingSplitGrid}>
          <div className={gymMarketingMainCol}>
            <div className={gymSocialCard}>
              <div className={gymMarketingCardInner}>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                  <h2 className={`${gymSocialTitleText} flex items-center gap-2`}>
                    <GitBranch className="h-5 w-5" />
                    {editingId ? t('gym.email.automations.editTitle') : t('gym.email.automations.newTitle')}
                  </h2>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className={editorMode === 'form' ? gymContentTabActive : gymContentTabInactive}
                      onClick={() => setEditorMode('form')}
                    >
                      {t('gym.email.automations.modeForm')}
                    </button>
                    <button
                      type="button"
                      className={editorMode === 'canvas' ? gymContentTabActive : gymContentTabInactive}
                      onClick={() => setEditorMode('canvas')}
                    >
                      <LayoutTemplate className="h-4 w-4 inline mr-1" />
                      {t('gym.email.automations.modeCanvas')}
                    </button>
                  </div>
                </div>

                {editorMode === 'canvas' ? (
                  <GymEmailAutomationFlowCanvas
                    triggerCode={triggerCode}
                    triggerLabel={triggerLabel}
                    triggerConfig={triggerConfig}
                    conditions={conditions}
                    steps={steps}
                    tags={tags}
                    templates={templates}
                    meta={meta}
                  />
                ) : (
                  <>
                    <p className={`${gymSocialMutedText} mb-4`}>{t('gym.email.automations.newHint')}</p>
                    <form onSubmit={handleSave} className="space-y-4">
                      <div>
                        <label className={gymModalLabel}>{t('gym.email.automations.name')}</label>
                        <input
                          className={gymModalInput}
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder={t('gym.email.automations.namePlaceholder')}
                        />
                      </div>

                      <div>
                        <label className={gymModalLabel}>{t('gym.email.automations.trigger')}</label>
                        <select
                          className={gymModalInput}
                          value={triggerCode}
                          onChange={(e) => handleTriggerChange(e.target.value)}
                        >
                          {visibleTriggers.map((tr) => (
                            <option key={tr.code} value={tr.code}>{tr.label}</option>
                          ))}
                        </select>
                      </div>

                      {renderTriggerConfig()}

                      <div className="space-y-2">
                        <p className={gymModalLabel}>{t('gym.email.automations.conditionsTitle')}</p>
                        {conditions.map((cond, index) => (
                          <div key={index} className={`flex flex-wrap items-center gap-2 p-2 ${gymSocialInsetBox}`}>
                            <span className={gymSocialItemText}>
                              {(meta.conditions || []).find((c) => c.code === cond.type)?.label || cond.type}
                            </span>
                            {(cond.type === 'has_tag' || cond.type === 'not_has_tag') && (
                              <select
                                className={`${gymModalInput} max-w-[200px]`}
                                value={cond.tag_id ?? ''}
                                onChange={(e) => updateConditionTag(index, e.target.value)}
                              >
                                <option value="">{t('gym.email.automations.selectTag')}</option>
                                {tags.map((tg) => (
                                  <option key={tg.id} value={tg.id}>{tg.name}</option>
                                ))}
                              </select>
                            )}
                            <button
                              type="button"
                              className={`${gymSocialBtnDanger} ml-auto`}
                              onClick={() => removeCondition(index)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                        {availableConditions.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {availableConditions.map((c) => (
                              <button
                                key={c.code}
                                type="button"
                                className={gymSocialBtnSecondary}
                                onClick={() => addCondition(c.code)}
                              >
                                <Plus className="h-3 w-3 inline mr-1" />
                                {c.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="space-y-3">
                        <p className={gymModalLabel}>{t('gym.email.automations.steps')}</p>
                        {steps.map((step, index) => (
                          <div key={index} className={`p-3 space-y-2 ${gymSocialInsetBox}`}>
                            <div className="flex items-center gap-2">
                              <span className={gymSocialStepIndex}>{index + 1}.</span>
                              <select
                                className={`${gymModalInput} flex-1`}
                                value={step.action_type}
                                onChange={(e) =>
                                  updateStep(index, {
                                    action_type: e.target.value,
                                    config: defaultStepConfig(e.target.value),
                                  })
                                }
                              >
                                {(meta.actions || []).map((act) => (
                                  <option key={act.code} value={act.code}>{act.label}</option>
                                ))}
                              </select>
                              {steps.length > 1 && (
                                <button
                                  type="button"
                                  className={`p-1 ${gymSocialBtnDanger}`}
                                  onClick={() => setSteps((prev) => prev.filter((_, i) => i !== index))}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                            {renderStepFields(step, index)}
                          </div>
                        ))}
                        <button
                          type="button"
                          className={gymSocialBtnSecondary}
                          onClick={() => setSteps((prev) => [...prev, { ...EMPTY_STEP }])}
                        >
                          <Plus className="h-4 w-4 inline mr-1" />
                          {t('gym.email.automations.addStep')}
                        </button>
                      </div>

                      <div className="flex flex-wrap gap-2 pt-2">
                        <button type="submit" className={gymSocialBtnPrimary} disabled={saving}>
                          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('gym.email.automations.save')}
                        </button>
                        {editingId && (
                          <button type="button" className={gymSocialBtnSecondary} onClick={resetForm}>
                            {t('gym.email.automations.cancelEdit')}
                          </button>
                        )}
                      </div>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className={gymMarketingSideCol}>
            <div className={gymSocialCard}>
              <div className={gymMarketingCardInner}>
                <h2 className={`${gymSocialTitleText} flex items-center gap-2`}>
                  <Zap className="h-5 w-5" />
                  {t('gym.email.automations.listTitle')}
                </h2>
                <p className={`${gymSocialMutedText} mt-1 mb-4`}>{t('gym.email.automations.listHint')}</p>

                {automations.length === 0 ? (
                  <p className={gymSocialMutedText}>{t('gym.email.automations.empty')}</p>
                ) : (
                  <ul className="space-y-2">
                    {automations.map((a) => {
                      const expanded = expandedListId === a.id;
                      return (
                      <li
                        key={a.id}
                        className={`p-3 flex flex-col gap-2 ${gymSocialInsetBox}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-left flex-1 min-w-0 break-words ${gymSocialListTitle}`}>
                            {a.name}
                          </p>
                          <button
                            type="button"
                            className={`${gymSocialBtnDanger} shrink-0`}
                            onClick={() => handleDelete(a.id)}
                            aria-label={t('gym.email.automations.delete')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        <p className={`${gymSocialMutedText} text-xs`}>
                          {(meta.triggers || []).find((tr) => tr.code === a.trigger_code)?.label || a.trigger_code}
                          {' · '}
                          {t('gym.email.automations.stepCount', { count: a.step_count || 0 })}
                          {' · '}
                          {t('gym.email.automations.runsCount', { count: a.runs_completed || 0 })}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            className={gymSocialBtnSecondary}
                            onClick={() => toggleListDetails(a.id)}
                          >
                            {listDetailsLoading === a.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin inline" />
                            ) : (
                              expanded ? t('gym.email.automations.listHideDetails') : t('gym.email.automations.listShowDetails')
                            )}
                          </button>
                          <button
                            type="button"
                            className={gymSocialBtnSecondary}
                            onClick={() => loadForEdit(a.id)}
                          >
                            <Pencil className="h-3.5 w-3.5 inline mr-1" />
                            {t('gym.email.automations.listEdit')}
                          </button>
                        </div>
                        {expanded && listDetailsCache[a.id] && (
                          <GymEmailAutomationRuleDetails
                            automation={listDetailsCache[a.id]}
                            meta={meta}
                            tags={tags}
                            templates={templates}
                          />
                        )}
                        <label className={`flex items-center gap-2 ${gymSocialControlLabel}`}>
                          <input
                            type="checkbox"
                            checked={!!a.is_active}
                            onChange={() => handleToggle(a.id, a.is_active)}
                          />
                          {t('gym.email.automations.active')}
                        </label>
                      </li>
                    );})}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
